import React, { useState } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Backdrop,
    CircularProgress,
    Card,
    CardContent,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
} from "@mui/material";
import { Analytics, RefreshSharp } from "@mui/icons-material";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import useNotification from "../common/Notification";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";

const MessagingLimits = () => {
    const [loading, setLoading] = useState(false);
    const [limits, setLimits] = useState(null);
    const showNotification = useNotification();

    const fetchMessagingLimits = async () => {
        setLoading(true);
        try {
            // Backend will automatically use config from database based on authenticated user
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/messaging-limits`
            );

            setLimits(response.data);
            showNotification("Messaging limits fetched successfully!", "success");
        } catch (error) {
            console.error("Error fetching messaging limits:", error);
            showNotification(
                error.response?.data?.error || "Failed to fetch messaging limits",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={WB_SX.page}>
            <Typography
                variant="h6"
                sx={WB_SX.pageTitle}
            >
                Messaging Limits & Analytics
            </Typography>

            <Backdrop
                sx={(theme) => ({
                    color: "#fff",
                    zIndex: theme.zIndex.drawer + 1,
                })}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <Card sx={{ mb: WB_UI.spacing.sectionGap, ...WB_SX.card }}>
                <CardContent sx={WB_SX.cardContent}>
                    <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                        <Button
                            variant="contained"
                            onClick={fetchMessagingLimits}
                            startIcon={<Analytics />}
                            sx={{ minWidth: "150px", ...WB_SX.primaryButton }}
                        >
                            Fetch Limits
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={fetchMessagingLimits}
                            startIcon={<RefreshSharp />}
                            sx={WB_SX.secondaryButton}
                        >
                            Refresh
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {limits && (
                <Card sx={WB_SX.card}>
                    <CardContent sx={WB_SX.cardContent}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 800, letterSpacing: "-0.02em" }}>
                            Messaging Limits
                        </Typography>
                        <Grid container spacing={2}>
                            {limits.tier && (
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Tier
                                            </Typography>
                                            <Typography variant="h5">
                                                <Chip
                                                    label={limits.tier}
                                                    color="primary"
                                                    size="medium"
                                                />
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}
                            {limits.monthly_limit && (
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Monthly Limit
                                            </Typography>
                                            <Typography variant="h5">
                                                {limits.monthly_limit.toLocaleString()}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}
                            {limits.daily_limit && (
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Daily Limit
                                            </Typography>
                                            <Typography variant="h5">
                                                {limits.daily_limit.toLocaleString()}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}
                        </Grid>

                        {limits.phone_numbers && limits.phone_numbers.length > 0 && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Phone Number Limits
                                </Typography>
                                <TableContainer component={Paper} elevation={0} sx={WB_SX.tableContainer}>
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: WB_UI.color.tableHeadBg }}>
                                                <TableCell sx={WB_SX.tableHeadCell}>
                                                    Phone Number
                                                </TableCell>
                                                <TableCell sx={WB_SX.tableHeadCell}>
                                                    Tier
                                                </TableCell>
                                                <TableCell sx={WB_SX.tableHeadCell}>
                                                    Monthly Limit
                                                </TableCell>
                                                <TableCell sx={WB_SX.tableHeadCell}>
                                                    Daily Limit
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {limits.phone_numbers.map((phone, index) => (
                                                <TableRow key={index} sx={WB_SX.tableRow}>
                                                    <TableCell>
                                                        {phone.display_phone_number || phone.id}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={phone.tier || "N/A"}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {phone.monthly_limit
                                                            ? phone.monthly_limit.toLocaleString()
                                                            : "N/A"}
                                                    </TableCell>
                                                    <TableCell>
                                                        {phone.daily_limit
                                                            ? phone.daily_limit.toLocaleString()
                                                            : "N/A"}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default MessagingLimits;

